import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { ErrorNote, Spinner } from '@/components/ui';
import { useSession } from '@/features/auth/session';
import { MfaGate } from '@/features/mfa/MfaGate';
import { platformAnalyticsService } from '@/services/api';

export const ANALYTICS_PERMISSION = 'analytics.view';
export const ENFORCE_PERMISSION = 'businesses.enforce';
export const GRANT_PLANS_PERMISSION = 'billing.grant';
export const EDIT_PLANS_PERMISSION = 'billing.plans';

// Which switched-on staff areas this person can open (staff only; others get nothing).
export function useStaffAccess() {
  const { data: session } = useSession();
  const isStaff = Boolean(session && session.user.platformRole !== 'NONE');
  const q = useQuery({
    queryKey: ['admin', 'me', 'access'],
    queryFn: platformAnalyticsService.myAccess,
    enabled: isStaff,
    staleTime: 60_000,
    retry: false,
  });
  return {
    loading: isStaff && q.isPending,
    error: q.error,
    canSeeAnalytics: q.data?.access[ANALYTICS_PERMISSION] === true,
    canEnforce: q.data?.access[ENFORCE_PERMISSION] === true,
    canGrantPlans: q.data?.access[GRANT_PLANS_PERMISSION] === true,
    canEditPlans: q.data?.access[EDIT_PLANS_PERMISSION] === true,
  };
}

// In front of every analytics page: two-step verification, then the analytics switch.
// The API enforces both; this only keeps others from seeing an empty page.
export function AnalyticsGate({ children }: { children: ReactNode }) {
  return (
    <MfaGate>
      <AccessCheck>{children}</AccessCheck>
    </MfaGate>
  );
}

function AccessCheck({ children }: { children: ReactNode }) {
  const access = useStaffAccess();
  if (access.loading) return <Spinner />;
  if (access.error) return <ErrorNote error={access.error} />;
  if (!access.canSeeAnalytics) return <Navigate to="/" replace />;
  return <>{children}</>;
}
